import React, { useState, useEffect } from 'react';
import { Table, Card, Tabs, Tag, Button, Modal, Form, Select, Input, Image, Popconfirm, Space, Typography, message, Row, Col, Badge } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, SearchOutlined, FileImageOutlined, VideoCameraOutlined, AudioOutlined, FileOutlined, AppstoreOutlined } from '@ant-design/icons';
import api from '../api';

const { Title, Text } = Typography;

const typeOptions = [
  { value: 'image', label: '图片', icon: <FileImageOutlined /> },
  { value: 'video', label: '视频', icon: <VideoCameraOutlined /> },
  { value: 'audio', label: '音频', icon: <AudioOutlined /> },
  { value: 'file', label: '文件', icon: <FileOutlined /> },
];

const typeLabels = {
  image: { label: '图片', color: 'blue' },
  video: { label: '视频', color: 'purple' },
  audio: { label: '音频', color: 'orange' },
  file: { label: '文件', color: 'default' },
};

function formatFileSize(bytes) {
  if (!bytes) return '-';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
}

function formatDuration(seconds) {
  if (!seconds) return '-';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function MaterialPage() {
  const [materials, setMaterials] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [activeType, setActiveType] = useState('image');
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const loadMaterials = async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 20, type: activeType };
      if (keyword) params.keyword = keyword;
      const { list, total: t } = await api.get('/admin/materials', { params });
      setMaterials(list);
      setTotal(t);
      setPage(p);
    } catch (e) {
      message.error('加载素材列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMaterials(1);
  }, [activeType]);

  const handleSearch = () => loadMaterials(1);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      await api.post('/admin/materials', values);
      message.success('素材上传成功');
      setCreateModal(false);
      form.resetFields();
      loadMaterials(page);
    } catch (e) {
      if (e.errorFields) return;
      message.error('上传失败');
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    editForm.setFieldsValue(record);
    setEditModal(true);
  };

  const handleUpdate = async () => {
    try {
      const values = await editForm.validateFields();
      await api.put(`/admin/materials/${editingRecord.id}`, values);
      message.success('更新成功');
      setEditModal(false);
      loadMaterials(page);
    } catch (e) {
      if (e.errorFields) return;
      message.error('更新失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/materials/${id}`);
      message.success('素材已删除');
      loadMaterials(page);
    } catch (e) {
      message.error('删除失败');
    }
  };

  const tabItems = [
    { key: 'all', label: <span><AppstoreOutlined /> 全部</span> },
    ...typeOptions.map(t => ({
      key: t.value,
      label: <span>{t.icon} {t.label}</span>,
    })),
  ];

  const handleTabChange = (key) => {
    setActiveType(key === 'all' ? '' : key);
    setPage(1);
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    {
      title: '名称', dataIndex: 'name', width: 200,
      render: (v, r) => (
        <Space>
          {r.type === 'image' && (
            <Image src={r.url} width={40} height={40} style={{ objectFit: 'cover', borderRadius: 4 }} preview />
          )}
          <span>{v}</span>
        </Space>
      ),
    },
    {
      title: '类型', dataIndex: 'type', width: 100,
      render: (v) => <Tag color={typeLabels[v]?.color}>{typeLabels[v]?.label || v}</Tag>,
    },
    { title: '大小', dataIndex: 'size', width: 100, render: (v) => formatFileSize(v) },
    {
      title: '尺寸', dataIndex: 'dimensions', width: 120,
      render: (v) => v || '-',
    },
    {
      title: '时长', dataIndex: 'duration', width: 100,
      render: (v) => (v ? formatDuration(v) : '-'),
    },
    { title: '上传者', dataIndex: 'uploader_name', width: 120, render: (v) => v || '-' },
    { title: '分类', dataIndex: 'category', width: 100, render: (v) => v ? <Tag>{v}</Tag> : '-' },
    { title: '标签', dataIndex: 'tags', width: 150, render: (v) => v ? v.split(',').map(t => <Tag key={t} style={{ marginBottom: 2 }}>{t.trim()}</Tag>) : '-' },
    { title: '上传时间', dataIndex: 'created_at', width: 180, render: (v) => v ? new Date(v).toLocaleString() : '-' },
    {
      title: '操作', width: 160,
      render: (_, r) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(r)}>编辑</Button>
          <Popconfirm title="确定删除该素材?" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4}>素材管理</Title>
        <Space>
          <Input
            placeholder="搜索名称或标签"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={handleSearch}
            prefix={<SearchOutlined />}
            style={{ width: 220 }}
          />
          <Button type="primary" onClick={handleSearch}>搜索</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); form.setFieldsValue({ type: activeType || 'image' }); setCreateModal(true); }} style={{ background: '#07C160' }}>
            上传素材
          </Button>
        </Space>
      </div>

      <Card>
        <Tabs activeKey={activeType || 'all'} items={tabItems} onChange={handleTabChange} style={{ marginBottom: 16 }} />
        <Table
          columns={columns}
          dataSource={materials}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            total,
            pageSize: 20,
            onChange: loadMaterials,
            showSizeChanger: false,
          }}
          size="small"
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* 上传素材 Modal */}
      <Modal title="上传素材" open={createModal} onOk={handleCreate} onCancel={() => setCreateModal(false)} width={600}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="素材名称" rules={[{ required: true, message: '请输入素材名称' }]}>
            <Input placeholder="输入素材名称" />
          </Form.Item>
          <Form.Item name="type" label="素材类型" rules={[{ required: true, message: '请选择类型' }]}>
            <Select placeholder="选择素材类型">
              {typeOptions.map(t => (
                <Select.Option key={t.value} value={t.value}>{t.icon} {t.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="url" label="资源地址" rules={[{ required: true, message: '请输入资源URL' }]}>
            <Input placeholder="https://example.com/image.png" />
          </Form.Item>
          <Form.Item name="category" label="分类">
            <Input placeholder="输入分类名称" />
          </Form.Item>
          <Form.Item name="tags" label="标签">
            <Input placeholder="多个标签用逗号分隔" />
          </Form.Item>
          <Form.Item name="size" label="文件大小 (字节)">
            <Input type="number" placeholder="文件大小（字节）" />
          </Form.Item>
          <Form.Item name="dimensions" label="尺寸 (仅图片/视频)">
            <Input placeholder="例如: 1920x1080" />
          </Form.Item>
          <Form.Item name="duration" label="时长 (秒, 仅视频/音频)">
            <Input type="number" placeholder="时长（秒）" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑素材 Modal */}
      <Modal title="编辑素材" open={editModal} onOk={handleUpdate} onCancel={() => setEditModal(false)} width={600}>
        <Form form={editForm} layout="vertical">
          <Form.Item name="name" label="素材名称" rules={[{ required: true, message: '请输入素材名称' }]}>
            <Input placeholder="输入素材名称" />
          </Form.Item>
          <Form.Item name="type" label="素材类型" rules={[{ required: true, message: '请选择类型' }]}>
            <Select placeholder="选择素材类型">
              {typeOptions.map(t => (
                <Select.Option key={t.value} value={t.value}>{t.icon} {t.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="url" label="资源地址" rules={[{ required: true, message: '请输入资源URL' }]}>
            <Input placeholder="https://example.com/image.png" />
          </Form.Item>
          <Form.Item name="category" label="分类">
            <Input placeholder="输入分类名称" />
          </Form.Item>
          <Form.Item name="tags" label="标签">
            <Input placeholder="多个标签用逗号分隔" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default MaterialPage;
